import { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`
const DEVICE_ID_KEY = 'kaizenos_pilot_device_id'

// Generate a unique device ID
const generateDeviceId = () => {
  return 'dev_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

// Get or create device ID from localStorage
const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY)
  if (!deviceId) {
    deviceId = generateDeviceId()
    localStorage.setItem(DEVICE_ID_KEY, deviceId)
  }
  return deviceId
}

// Detect device type
const detectDeviceType = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera
  
  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios'
  }
  
  // Android detection
  if (/android/i.test(userAgent)) {
    return 'android'
  }
  
  // Default to desktop
  return 'desktop'
}

// Detect if running as installed PWA
const detectPWA = () => {
  // Check for standalone mode (iOS)
  if (window.navigator.standalone === true) {
    return true
  }
  
  // Check for display-mode: standalone (Android/Desktop)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true
  }
  
  // Check for display-mode: fullscreen
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return true
  }
  
  // Check for display-mode: minimal-ui
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return true
  }
  
  return false
}

export const usePilotTracking = () => {
  const [pilotCount, setPilotCount] = useState(null)
  const [isTracked, setIsTracked] = useState(false)
  const { token, isAuthenticated } = useAuth()

  // Track pilot user on first visit
  useEffect(() => {
    const trackVisit = async () => {
      const deviceId = getOrCreateDeviceId()
      const deviceType = detectDeviceType()
      const installedPwa = detectPWA()
      
      try {
        await axios.post(`${API}/pilot/track`, {
          device_id: deviceId,
          device_type: deviceType,
          installed_pwa: installedPwa
        })
        setIsTracked(true)
      } catch (error) {
        console.error('Error tracking pilot user:', error)
      }
    }

    trackVisit()
  }, [])

  // Link pilot to user when authenticated
  useEffect(() => {
    const linkToUser = async () => {
      if (!isAuthenticated || !token) return
      
      const deviceId = localStorage.getItem(DEVICE_ID_KEY)
      if (!deviceId) return
      
      try {
        await axios.post(`${API}/pilot/link-user`, 
          { device_id: deviceId },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      } catch (error) {
        console.error('Error linking pilot to user:', error)
      }
    }

    linkToUser()
  }, [isAuthenticated, token])

  // Fetch pilot count
  const fetchPilotCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pilot/count`)
      setPilotCount(response.data.count)
      return response.data.count
    } catch (error) {
      console.error('Error fetching pilot count:', error)
      return null
    }
  }, [])

  // Fetch count on mount
  useEffect(() => {
    fetchPilotCount()
  }, [fetchPilotCount])

  return {
    pilotCount,
    isTracked,
    fetchPilotCount,
    deviceType: detectDeviceType(),
    isPWA: detectPWA()
  }
}

export default usePilotTracking
